import { useEffect, useMemo, useRef, useState } from "react";
import deepdash from "deepdash";
import lodash from "lodash";
import { lang } from "config";
import nProgress from "nprogress";
import toast from "react-hot-toast";

// ── shared components you already use
import Label from "components/forms/label";
import Input from "components/forms/input";
import Description from "components/forms/description";
import ErrorMessage from "components/forms/error-message";
import SelectSearchable from "components/forms/select-searchable";
import { X, PhotoOff } from "components/icons/outline";
import InputImage from "components/forms/input-image";
import Checkbox from "components/forms/checkbox";
import ErrorTooltip from "components/forms/error-tooltip";
import BarcodeScanner from "components/forms/barcode-scanner";

// ── child adders (unchanged)
import { Add as AddAttribute } from "../unit/attribute";
import { Add as AddPrice } from "../unit/price";
import { Add as AddDiscount } from "../unit/discount";
import { Add as AddReward } from "../unit/reward";
import { Add as AddStock } from "../unit/stock";

// ── lookups
import useProductSeriesRepositories from "repositories/product-series";
import { usePage } from "contexts/page-context";

// ── API helpers (use your real repos/services)
import axios from "axios";
import moment from "moment";

import AsyncModelPicker from "./AsyncModalPicker";
import AsyncUnitPicker from "./AsyncUnitPicker";
import { useAuth } from "contexts/auth-context";

export default function FormModelAndUnit({
  errors = {},
  onDone, // optional callback after success
  useSingleEndpoint = true, // set true if you add a combined endpoint
}) {
  const _ = deepdash(lodash);
  const { configuration } = usePage();
  const {user} = useAuth();
  
  const slugify = (s) => lodash.kebabCase(String(s || "").trim()).toLowerCase();

  const normalizeTag = (t) => {
    // cover berbagai bentuk: langsung, nested, atau lewat pivot
    const id   = t?.id ?? t?.tag?.id ?? t?.tag_id ?? t?.pivot?.tag_id ?? null;
    const name = t?.name ?? t?.tag?.name ?? t?.label ?? "";
    const slug =
      t?.slug ??
      t?.tag?.slug ??
      t?.code ??
      t?.pivot?.slug ?? // kalau memang pernah disimpan di pivot
      slugify(name);

    return { id, name, slug };
  };

  // ─────────────────────────────────────────────────────────
  // LOOKUPS
  // ─────────────────────────────────────────────────────────
  const {
    data: productSeriesSelection = [],
    isLoading: loadingSeries,
  } = useProductSeriesRepositories({
    paginate: false,
    order_by: "series.name",
    direction: "asc",
  });

  // ─────────────────────────────────────────────────────────
  // MODEL STATE (from your Product Model form)
  // ─────────────────────────────────────────────────────────
  const [modelName, setModelName] = useState("");
  const [modelLongName, setModelLongName] = useState("");
  const [productSeriesId, setProductSeriesId] = useState(null);
  const [productSeriesObj, setProductSeriesObj] = useState(null);
  const [tags, setTags] = useState([]);
  const [copying, setCopying] = useState(false);

  const [dupModel, setDupModel] = useState(null);
  const [dupUnitLight, setDupUnitLight] = useState(null);
  // state baru
  const [previewSrc, setPreviewSrc] = useState(null);
  // image tetap File/Blob/null

    // fetch ONE unit with full relations only when user clicks “Copy Unit”
  async function fetchUnitDetail(unitId) {
    const baseURL = process.env.REACT_APP_INVENTORY_API_URL;
    const url = new URL(`${baseURL}/product/unit/${unitId}`);
    // return just needed relations:
    url.searchParams.append("include",
        [
        "prices",
        "discounts.discount",   // include discount object
        "rewards.reward",       // include reward object
        "attributeDataPivots.attribute",
        "attributeDataPivots.attributeGroup",
        "groupItems.productUnit.productModel",
        // stocks usually big — skip cloning live stocks; we won’t include
        ].join(",")
    );
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return json?.data ?? json;
    }

    async function handleCopyUnit() {
        if (!dupModel || !dupUnitLight) return;
        try {
            setCopying(true);
            nProgress.start();

            // prefill MODEL (from light dupModel)
            setModelName(dupModel?.name ?? "");
            setModelLongName(dupModel?.long_name ?? "");
            setProductSeriesId(dupModel?.product_series_id ?? dupModel?.product_series?.id ?? null);
            setProductSeriesObj(dupModel?.product_series ?? null);
            setTags((dupModel?.tags ?? []).map(normalizeTag));
            console.log(dupModel?.tags,"tags")
            // fetch ONE unit full detail
            const full = await fetchUnitDetail(dupUnitLight.id);
            console.log(full.pre_order,!!full.pre_order,"preorder")
            // map into Unit
            setIsBundle(!!full.is_bundle);
            setIsPreOrder(!!full.pre_order);
            setUnitName(full?.name ?? "");
            setReferredId(full?.id ?? "");
            setUnitLongName(full?.long_name ?? "");
            setSku(""); // avoid collision
            setImage(null);                      // default: tidak upload file baru
            setPreviewSrc(full?.image || null);  // tampilkan gambar lama (URL)
            console.log(full?.image,"full image")
            setAttribute((full?.attribute_data_pivots ?? []).map((r)=>({
              attribute_id: r.attribute_id ?? r.attribute?.id,
              attribute_group_id: r.attribute_group_id ?? r.attribute_group?.id,
              attribute: r.attribute, attribute_group: r.attribute_group
            })));

            // setPrice((full?.prices ?? []).map((p)=>({
            // active_date: p.active_date,
            // maximum_retail_price: p.maximum_retail_price,
            // price: p.price
            // })));

            setDiscount((full?.discount_pivots ?? []).map((d)=>({
            discount: d.discount,
            discount_type_id: d.discount_type_id ?? d.discount?.id,
            from_date: d.from_date,
            to_date: d.to_date
            })));

            setReward((full?.reward_pivots ?? []).map((r)=>({
            reward: r.reward,
            reward_type_id: r.reward_type_id ?? r.reward?.id,
            from_date: r.from_date,
            to_date: r.to_date
            })));

            setStock((full?.stocks ?? []).map((s)=>({
            purchase_date: s.purchase_date,
            dos_date: s.dos_date,
            outlet_id: s.outlet_id,
            purchase_order_id: s.purchase_order_id,
            purchase_stock: s.purchase_stock,
            current_stock: s.current_stock,
            amount: s.amount,
            hpp_amount: s.hpp_amount,
            maximum_retail_price: s.maximum_retail_price,
            })));

            setBundle((full?.group_items ?? []).map((b)=>({
            id: b.product_unit_id ?? b?.product_unit?.id,
            name: b.product_unit?.name,
            long_name: b.product_unit?.long_name,
            sku: b.product_unit?.sku,
            image: b.product_unit?.image,
            product_model: b.product_unit?.product_model,
            })));

            toast.success("Unit copied into form");
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.message || "Gagal menyalin unit");
        } finally {
            setCopying(false);
            nProgress.done();
        }
    }


  useEffect(() => {
    if (!loadingSeries && !configuration("hierarchy").find(r => r.key==="product-series")?.value) {
      // if product-series is hidden + default 'auto', preselect first
      const cfg = configuration("hierarchy").find(r => r.key==="product-model");
      if (cfg?.default_parent === "auto" && productSeriesSelection?.[0]?.id) {
        setProductSeriesId(productSeriesSelection[0].id);
      }
    }
  }, [loadingSeries, productSeriesSelection, configuration]);

  useEffect(() => {
    if (productSeriesId && !loadingSeries) {
      setProductSeriesObj(productSeriesSelection.find((r) => r.id === productSeriesId) || null);
    } else {
      setProductSeriesObj(null);
    }
  }, [productSeriesId, loadingSeries, productSeriesSelection]);

  useEffect(() => {
    // mirror long name while typing
    setModelLongName(m => (m || modelName));
  }, [modelName]);

  useEffect(() => {
    setTags((prev) => prev.map(normalizeTag));
  }, [dupModel]);


  const handleAddTag = (payload) => {
    const rows = [
      ...(payload?.product_type_rows || []),
      ...(payload?.variant_rows || []),
    ].map(normalizeTag);

    setTags((prev) => [...prev, ...rows]);
  };

  const handleRemoveTag = (idx) => setTags(prev => prev.filter((_, i) => i !== idx));

  // ─────────────────────────────────────────────────────────
  // UNIT STATE (from your Product Unit form)
  // ─────────────────────────────────────────────────────────
  const [isBundle, setIsBundle] = useState(false);
  const [bundle, setBundle] = useState([]);
  const [isPreOrder, setIsPreOrder] = useState(false);

  const [unitName, setUnitName] = useState("");
  const [unitLongName, setUnitLongName] = useState("");
  const [sku, setSku] = useState("");
  const [referred_id, setReferredId] = useState("");
  const [image, setImage] = useState(null);

  // maps
  const [price, setPrice] = useState([]);
  const [discount, setDiscount] = useState([]);
  const [reward, setReward] = useState([]);
  const [attribute, setAttribute] = useState([]);
  const [stock, setStock] = useState([]);

  useEffect(() => {
    setUnitLongName(u => (u || unitName));
  }, [unitName]);

  // bundle/attribute coupling (same as your original)
  useEffect(() => {
    if (isBundle) {
      setSku("");
      setStock([]);
    } else {
      setBundle([]);
    }
  }, [isBundle]);

  // add/remove helpers (same as your original)
  const handleAddPrice = (d) => setPrice(prev => [...prev, d]);
  const handleRemovePrice = (i) => setPrice(prev => prev.filter((_, idx) => idx !== i));
  const handleAddDiscount = (d) => setDiscount(prev => [...prev, d]);
  const handleRemoveDiscount = (i) => setDiscount(prev => prev.filter((_, idx) => idx !== i));
  const handleAddReward = (d) => setReward(prev => [...prev, d]);
  const handleRemoveReward = (i) => setReward(prev => prev.filter((_, idx) => idx !== i));
  const handleAddAttribute = (d) => setAttribute(prev => [...prev, d]);
  const handleRemoveAttribute = (i) => setAttribute(prev => prev.filter((_, idx) => idx !== i));
  const handleAddStock = (d) => setStock(prev => [...prev, d]);
  const handleRemoveStock = (i) => setStock(prev => prev.filter((_, idx) => idx !== i));

  async function urlToFile(url, filename = 'image.jpg') {
    const res = await fetch(url, { cache: 'no-store' });
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || 'image/jpeg' });
  }

  // ─────────────────────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    if (nProgress.isStarted()) {
      toast.error("Previous action is loading");
      return;
    }

    // 1) build model payload
    const modelPayload = {
      name: modelName,
      long_name: modelLongName,
      product_series_id: productSeriesId,
      product_series: productSeriesObj, // if your API doesn’t need this, drop it
      tags, // keep same as original
      tag_ids: tags.map(t => t.id),
      created_by: user?.name, // optional
    };

    // 2) build unit payload (same as original Unit form), EXCEPT product_model_id — we’ll fill it after model is created
    const discountData = discount.map((row) => ({ ...row, discount_type_id: row?.discount?.id }));
    const rewardData   = reward.map((row) => ({ ...row, reward_type_id: row?.reward?.id }));
    const bundleData   = bundle.map((row) => ({ product_unit_id: row.id }));
    const stockData    = stock.map((row) => Object.fromEntries(
      Object.entries(row).filter(([_, v]) => (v != null && v !== '') || v === 0)
    ));

    const unitPayloadBase = {
      name: unitName,
      referred_id: isPreOrder ? referred_id : null,
      long_name: unitLongName,
      sku: isBundle ? null : sku,
      image,
      pre_order: isPreOrder ? 1 : 0,
      // product_model_id: <— fill after model create
      attribute,
      price,
      discount: discountData,
      reward: rewardData,
      stock: stockData,
      is_bundle: isBundle,
      bundle: bundleData,
    };
    console.log(isPreOrder,unitPayloadBase.pre_order)
    const fd = new FormData();

    // MODEL
    fd.append('model[name]', modelPayload.name);
    fd.append('model[long_name]', modelPayload.long_name ?? '');
    fd.append('model[product_series_id]', String(modelPayload.product_series_id));
    (modelPayload.tag_ids || []).forEach((id, i) =>
      fd.append(`model[tag_ids][${i}]`, String(id))
    );

    // (opsional) kirim objek lengkap, kalau endpoint mendukung:
    tags.forEach((t, i) => {
      fd.append(`model[tags][${i}][id]`, String(t.id));
      fd.append(`model[tags][${i}][slug]`, t.slug);
      fd.append(`model[tags][${i}][name]`, t.name);
    });

    fd.append('created_by', modelPayload.created_by ?? '');

    // UNIT (flat)
    fd.append('unit[name]', unitPayloadBase.name);
    fd.append('unit[long_name]', unitPayloadBase.long_name ?? '');
    fd.append('unit[referred_id]', unitPayloadBase.referred_id ?? '');
    if (unitPayloadBase.sku) fd.append('unit[sku]', unitPayloadBase.sku);
    fd.append('unit[pre_order]', unitPayloadBase.pre_order ? '1' : '0');
    fd.append('unit[is_bundle]', unitPayloadBase.is_bundle ? '1' : '0');

    // FILE — make sure "image" is a File/Blob
    if (unitPayloadBase.image) {
    fd.append('unit[image]', unitPayloadBase.image); // matches $request->file('unit.image')
    }

    // ARRAYS
    unitPayloadBase.price?.forEach((p, i) => {
    fd.append(`unit[price][${i}][active_date]`, p.active_date);
    fd.append(`unit[price][${i}][maximum_retail_price]`, String(p.maximum_retail_price ?? ''));
    fd.append(`unit[price][${i}][price]`, String(p.price));
    });

    unitPayloadBase.stock?.forEach((s, i) => {
    fd.append(`unit[stock][${i}][purchase_date]`, s.purchase_date);
    if (s.dos_date) fd.append(`unit[stock][${i}][dos_date]`, s.dos_date);
    if (s.outlet_id) fd.append(`unit[stock][${i}][outlet_id]`, String(s.outlet_id));
    if (s.purchase_order_id) fd.append(`unit[stock][${i}][purchase_order_id]`, s.purchase_order_id);
    fd.append(`unit[stock][${i}][purchase_stock]`, String(s.purchase_stock));
    fd.append(`unit[stock][${i}][current_stock]`, String(s.current_stock));
    fd.append(`unit[stock][${i}][amount]`, String(s.amount));
    fd.append(`unit[stock][${i}][hpp_amount]`, String(s.hpp_amount));
    if (s.maximum_retail_price) fd.append(`unit[stock][${i}][maximum_retail_price]`, String(s.maximum_retail_price));
    });

    unitPayloadBase.attribute?.forEach((a, i) => {
    fd.append(`unit[attribute][${i}][attribute_id]`, String(a.attribute_id));
    fd.append(`unit[attribute][${i}][attribute_group_id]`, String(a.attribute_group_id));
    });

    unitPayloadBase.discount?.forEach((d, i) => {
    fd.append(`unit[discount][${i}][discount_type_id]`, String(d.discount_type_id));
    fd.append(`unit[discount][${i}][from_date]`, d.from_date);
    fd.append(`unit[discount][${i}][to_date]`, d.to_date);
    });

    unitPayloadBase.reward?.forEach((r, i) => {
    fd.append(`unit[reward][${i}][reward_type_id]`, String(r.reward_type_id));
    fd.append(`unit[reward][${i}][from_date]`, r.from_date);
    fd.append(`unit[reward][${i}][to_date]`, r.to_date);
    });

    if (!image && previewSrc) {
      const file = await urlToFile(previewSrc, 'copied.jpg');
      fd.append('unit[image]', file);
    }
    // console.log(fd,"asu")

    try {
      nProgress.start();

      // OPTION A: Single combined endpoint
      if (useSingleEndpoint) {
        const { data } = await axios.post(process.env.REACT_APP_INVENTORY_API_URL+"/product/models/with-first-unit", fd);
        toast.success("Model & Unit created");
        // onDone?.({ model: data?.model, unit: data?.unit });
        window.location.reload();
        return;
      }

      // OPTION B: Two-step create
      const createModel = await axios.post("/api/product-models", modelPayload);
      const createdModel = createModel?.data?.data || createModel?.data;

      const unitPayload = {
        ...unitPayloadBase,
        product_model_id: createdModel?.id,
      };

      const createUnit = await axios.post("/api/product-units", unitPayload);
      const createdUnit = createUnit?.data?.data || createUnit?.data;

      toast.success("Model & Unit created");
      window.location.reload();
    //   onDone?.({ model: createdModel, unit: createdUnit });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to create Model & Unit");
    } finally {
      nProgress.done();
    }
  }

  const inputImageKey = useMemo(
    () => `input-image-${previewSrc ?? 'empty'}`,
    [previewSrc]
  );

  // helper
  const showSeries = configuration("hierarchy").find((r)=>r.key==="product-series")?.value;

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-10">
      {/* ───────────────────── MODEL SECTION ───────────────────── */}
      <section>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Product Model</h3>
              <p className="mt-1 text-sm text-gray-600">Buat model terlebih dahulu.</p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <section className="mb-6">
                <div className="border sm:rounded-xl">
                    <div className="px-4 py-4 sm:p-6 grid gap-4 sm:grid-cols-3 items-start">
                    <div>
                        <label className="block text-xs text-neutral-700">Pilih Model</label>
                        <AsyncModelPicker
                        value={dupModel?.id}
                        onChange={(m)=>{ setDupModel(m); setDupUnitLight(null); }}
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-neutral-700">Pilih Unit</label>
                        <AsyncUnitPicker
                        modelId={dupModel?.id}
                        value={dupUnitLight?.id}
                        onChange={(u)=>setDupUnitLight(u)}
                        />
                    </div>
                    <div className="flex sm:justify-end items-end">
                        <button
                        type="button"
                        disabled={!dupModel || !dupUnitLight || copying}
                        onClick={handleCopyUnit}
                        className="px-4 py-2 bg-neutral-800 text-white rounded-xl disabled:opacity-60 inline-flex items-center gap-2"
                        >
                        {copying && (
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 004 12z"/>
                            </svg>
                        )}
                        {copying ? "Copying…" : "Copy Unit"}
                        </button>

                    </div>
                    </div>
                </div>
                </section>

            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 sm:p-6 grid grid-cols-2 gap-4">
                {showSeries && (
                  <>
                    <div className="col-span-2 sm:col-span-1">
                      <Label htmlFor="series" value="Product Series" />
                      <SelectSearchable
                        filter={(v) => v.series?.name}
                        onChange={(v) => setProductSeriesId(v)}
                        selection={productSeriesSelection}
                        isLoading={loadingSeries}
                        value={productSeriesId}
                        keyValue={(v) => v.id}
                        title={(v) => v.series?.name}
                        description={(v) => `${v.product_brand?.product_type?.name} – ${v.product_brand?.brand?.name}`}
                        placeholder="Select Product Series"
                        error={errors?.product_series_id}
                        reverse
                      />
                      <ErrorMessage error={errors?.product_series_id} />
                    </div>
                    <div />
                  </>
                )}
                <div>
                  <Label htmlFor="model_name" value="Model Name" />
                  <Input id="model_name" placeholder="Input name here"
                    onChange={(e)=>setModelName(e.target.value)} value={modelName} error={errors?.model_name}/>
                  <Description error={errors?.model_name} />
                </div>
                <div>
                  <Label htmlFor="model_long_name" value="Model Long Name" />
                  <Input id="model_long_name" placeholder="Input long name here"
                    onChange={(e)=>setModelLongName(e.target.value)} value={modelLongName} error={errors?.model_long_name}/>
                  <Description error={errors?.model_long_name} />
                </div>

                {/* Tags table kept simple */}
                <div className="col-span-2">
                  <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Slug</th>
                          <th className="px-6 py-3" />
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {tags.length === 0 ? (
                          <tr><td colSpan={2} className="px-6 py-4 text-xs text-neutral-500 text-center">{lang.no_data}</td></tr>
                        ) : tags.map((t, i)=>(
                          <tr key={`${t.slug}-${i}`}>
                            <td className="px-6 py-3 text-xs">{t.slug}</td>
                            <td className="px-6 py-3 text-right">
                              <button type="button" onClick={()=>handleRemoveTag(i)} className="bg-red-800 inline-flex items-center p-1 text-white rounded-full">
                                <X className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* reuse your AddTag component here if you want; keeping it simple */}
                  {/* <AddTag tags={tags} onSubmit={handleAddTag} /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="hidden sm:block" aria-hidden="true"><div className="py-5"><div className="border-t border-gray-200" /></div></div>

      {/* ───────────────────── UNIT SECTION ───────────────────── */}
      <section>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Product Unit</h3>
              <p className="mt-1 text-sm text-gray-600">Unit pertama untuk model ini.</p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="p-4 sm:p-6 grid gap-4 sm:grid-cols-2">
                {/* <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="image" value="Image" />
                    {copying && (
                        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center text-xs rounded-xl">
                        Loading image…
                        </div>
                    )}
                    <InputImage key={inputImageKey} onChange={(value) => setImage(value)} aspect="square" id="image" name="image" src={previewSrc} error={errors.image} alt="https://placehold.co/400x400/F5F5F5/404040?font=source-sans-pro&text=400x400" />
                  <ErrorMessage error={errors?.image} />
                </div> */}

                {/* We REMOVE model dropdown here. It will bind to the newly created model */}
                <div className="col-span-2 sm:col-span-1 space-y-4">
                  {!isBundle && (
                    <div>
                      <Label htmlFor="sku" value="SKU" />
                      <div className="flex space-x-2">
                        <Input placeholder="000-SKU-123" onChange={(e)=>setSku(e.target.value)} value={sku} id="sku" error={errors?.sku} />
                        <BarcodeScanner onScan={(val)=>setSku(val)} />
                      </div>
                      <ErrorMessage error={errors?.sku} />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="unit_name" value="Unit Name" />
                    <Input placeholder="Caliburn AK3 - White" onChange={(e)=>setUnitName(e.target.value)} value={unitName} id="unit_name" error={errors?.name} />
                    <Description error={errors?.name} />
                  </div>
                  <div>
                    <Label htmlFor="unit_long_name" value="Unit Long Name" />
                    <Input placeholder="Uwell Caliburn AK3 - White" onChange={(e)=>setUnitLongName(e.target.value)} value={unitLongName} id="unit_long_name" error={errors?.long_name} />
                    <Description error={errors?.long_name} />
                  </div>
                  {configuration("site")==="hq" && (
                    <div className="col-span-2">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <Checkbox id="is_pre_order" checked={isPreOrder} onChange={()=>setIsPreOrder(!isPreOrder)} />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="is_pre_order" className="font-medium text-gray-700">Pre-order</label>
                          <p className="text-gray-500">Check if the product is pre-order.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* {isPreOrder && <div>
                    <Label htmlFor="referred_id" value="Referred ID" />
                    <div className="flex space-x-2">
                      <Input placeholder="Product Unit Id" onChange={(e)=>setReferredId(e.target.value)} value={referred_id} id="referred_id" error={errors?.referred_id} />
                    </div>
                    <ErrorMessage error={errors?.referred_id} />
                  </div>} */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attributes */}
        <div className="hidden sm:block" aria-hidden="true"><div className="py-5"><div className="border-t border-gray-200" /></div></div>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Data Atribut</h3>
              <p className="mt-1 text-sm text-gray-600">Contoh: warna, rasa, dll.</p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 space-y-4 sm:p-6">
                <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Group</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Attribute</th>
                        <th className="px-6 py-3" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {attribute.length === 0 ? (
                        <tr><td colSpan={3} className="px-6 py-4 text-xs text-neutral-500 text-center">{lang.no_data}</td></tr>
                      ) : attribute.map((row, idx)=>(
                        <tr key={`${row?.attribute_id}-${idx}`}>
                          <td className="px-6 py-3 text-xs">{row?.attribute_group?.name}</td>
                          <td className="px-6 py-3 text-xs">{row?.attribute?.name}</td>
                          <td className="px-6 py-3 text-right">
                            <button type="button" onClick={()=>handleRemoveAttribute(idx)} className="bg-red-800 inline-flex items-center p-1 text-white rounded-full">
                              <X className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {configuration("site")==="hq" && (
                  <div className="text-xs">
                    <AddAttribute data={attribute} onSubmit={handleAddAttribute} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stock */}
        {!isBundle && (
          <>
            <div className="hidden sm:block" aria-hidden="true"><div className="py-5"><div className="border-t border-gray-200" /></div></div>
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <div className="px-4 sm:px-0">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Data Stock</h3>
                  <p className="mt-1 text-sm text-gray-600">List data stock produk.</p>
                </div>
              </div>
              <div className="mt-5 md:col-span-2 md:mt-0">
                <div className="border sm:rounded-xl">
                  <div className="px-4 py-5 space-y-4 sm:p-6">
                    <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                      <table className="min-w-full divide-y divide-neutral-200">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Purchase Date</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">DoS Date</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Outlet ID</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">PO ID</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Purchase Stock</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Current Stock</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Amount</th>
                            <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">HPP</th>
                            {configuration("site")==="go" && (
                              <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">MRP</th>
                            )}
                            <th className="px-6 py-3" />
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-neutral-200">
                          {stock.length === 0 ? (
                            <tr><td colSpan={9} className="px-6 py-4 text-xs text-neutral-500 text-center">{lang.no_data}</td></tr>
                          ) : stock.map((row, idx)=>(
                            <tr key={`${row?.purchase_order_id}-${idx}`}>
                              <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                {moment(row.purchase_date).format('MMMM D, YYYY')}
                              </td>
                              <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                {row.dos_date ? moment(row.dos_date).format('MMMM D, YYYY') : '-'}
                              </td>
                              <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{row.outlet_id ?? '-'}</td>
                              <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                <div className="inline-flex items-center space-x-2">
                                  <span>{row.purchase_order_id ?? '-'}</span>
                                  <ErrorTooltip error={errors?.[`stock.${idx}.purchase_order_id`]} />
                                </div>
                              </td>
                              <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{row.purchase_stock}</td>
                              <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{row.current_stock}</td>
                              <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                {Intl.NumberFormat('id-Id', { style: 'currency', currency: "IDR" }).format(row.amount)}
                              </td>
                              <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                {Intl.NumberFormat('id-Id', { style: 'currency', currency: "IDR" }).format(row.hpp_amount)}
                              </td>
                              {configuration("site")==="go" && (
                                <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                  {Intl.NumberFormat('id-Id', { style: 'currency', currency: "IDR" }).format(row.maximum_retail_price)}
                                </td>
                              )}
                              <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                <button type="button" onClick={()=>handleRemoveStock(idx)} className="bg-red-800 inline-flex items-center p-1 text-white rounded-full">
                                  <X className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {configuration("site")==="hq" && (
                      <div className="text-xs">
                        <AddStock onSubmit={handleAddStock} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Prices */}
        <div className="hidden sm:block" aria-hidden="true"><div className="py-5"><div className="border-t border-gray-200" /></div></div>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Data Harga</h3>
              <p className="mt-1 text-sm text-gray-600">List harga berlaku.</p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 space-y-4 sm:p-6">
                <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Active Date</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">MRP</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Price</th>
                        <th className="px-6 py-3" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {price.length === 0 ? (
                        <tr><td colSpan={4} className="px-6 py-4 text-xs text-neutral-500 text-center">{lang.no_data}</td></tr>
                      ) : price.map((row, idx)=>(
                        <tr key={`${row?.active_date}-${idx}`}>
                          <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">{moment(row.active_date).format('MMMM D, YYYY')}</td>
                          <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                            {Intl.NumberFormat('id-Id',{ style:'currency', currency:"IDR"}).format(row.maximum_retail_price)}
                          </td>
                          <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                            {Intl.NumberFormat('id-Id',{ style:'currency', currency:"IDR"}).format(row.price)}
                          </td>
                          <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                            <button type="button" onClick={()=>handleRemovePrice(idx)} className="bg-red-800 inline-flex items-center p-1 text-white rounded-full">
                              <X className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs">
                  <AddPrice priceData={price} stockData={stock} onSubmit={handleAddPrice} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Discount */}
        <div className="hidden sm:block" aria-hidden="true"><div className="py-5"><div className="border-t border-gray-200" /></div></div>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Data Diskon</h3>
              <p className="mt-1 text-sm text-gray-600">Promo/discount yang berlaku.</p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 space-y-4 sm:p-6">
                <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Discount</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">From</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">To</th>
                        <th className="px-6 py-3" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {discount.length === 0 ? (
                        <tr><td colSpan={4} className="px-6 py-4 text-xs text-neutral-500 text-center">{lang.no_data}</td></tr>
                      ) : discount.map((row, idx)=>(
                        <tr key={`${row?.discount?.id}-${idx}`}>
                          <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">{row?.discount?.name}</td>
                          <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{moment(row.from_date).format('MMMM D, YYYY')}</td>
                          <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{moment(row.to_date).format('MMMM D, YYYY')}</td>
                          <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                            <button type="button" onClick={()=>handleRemoveDiscount(idx)} className="bg-red-800 inline-flex items-center p-1 text-white rounded-full">
                              <X className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs">
                  <AddDiscount data={discount} onSubmit={handleAddDiscount} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reward */}
        <div className="hidden sm:block" aria-hidden="true"><div className="py-5"><div className="border-t border-gray-200" /></div></div>
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Data Reward</h3>
              <p className="mt-1 text-sm text-gray-600">Poin/reward produk.</p>
            </div>
          </div>
          <div className="mt-5 md:col-span-2 md:mt-0">
            <div className="border sm:rounded-xl">
              <div className="px-4 py-5 space-y-4 sm:p-6">
                <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Reward</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">From</th>
                        <th className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">To</th>
                        <th className="px-6 py-3" />
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {reward.length === 0 ? (
                        <tr><td colSpan={4} className="px-6 py-4 text-xs text-neutral-500 text-center">{lang.no_data}</td></tr>
                      ) : reward.map((row, idx)=>(
                        <tr key={`${row?.reward?.id}-${idx}`}>
                          <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">{row?.reward?.name}</td>
                          <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{moment(row.from_date).format('MMMM D, YYYY')}</td>
                          <td className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">{moment(row.to_date).format('MMMM D, YYYY')}</td>
                          <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                            <button type="button" onClick={()=>handleRemoveReward(idx)} className="bg-red-800 inline-flex items-center p-1 text-white rounded-full">
                              <X className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs">
                  <AddReward data={reward} onSubmit={handleAddReward} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── SUBMIT ───────────────────── */}
      <div className="mt-8 text-xs text-right">
        <button type="submit" className="items-center px-6 py-3 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
          <span>{lang.create}</span>
        </button>
      </div>
    </form>
  );
}
